import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Network, RefreshCcw, AlertTriangle } from "lucide-react";

import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader, ResizableSidebar } from "@/components/common";
import ConfirmDialog from "@/features/user-management/components/ConfirmDialog";
import { useOrganizationStore } from "../hooks";
import OrganizationTree from "../components/OrganizationTree";
import OrganizationDetails from "../components/OrganizationDetails";
import OrganizationUnitDialog from "../components/OrganizationUnitDialog";
import OrganizationDiagram from "../components/OrganizationDiagram";
import { List } from "lucide-react";
import { PlusCircle } from "lucide-react";

const LEVEL_REGEX = /^level_(\d+)$/;

const resolveGroupBy = (mode) => {
  if (mode === "businessType") return "businessType";
  if (mode === "country") return "country";
  return undefined;
};

const getLevelNumber = (unitLevel) => {
  if (!unitLevel) return null;
  const match = LEVEL_REGEX.exec(unitLevel);
  if (!match) return null;
  const level = Number(match[1]);
  return Number.isFinite(level) && level > 0 ? level : null;
};

const normalizeKey = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const hasMatchFlag = (node) => {
  if (!node) return false;
  if (typeof node.matchesGroup === "boolean") return true;
  return Array.isArray(node.children)
    ? node.children.some((child) => hasMatchFlag(child))
    : false;
};

const matchesGroupFallback = (node, groupMode, groupKey) => {
  if (!node || node.isGroup) return false;
  const normalizedKey = normalizeKey(groupKey);

  if (groupMode === "businessType") {
    return Array.isArray(node.businessTypes)
      ? node.businessTypes.some((type) => normalizeKey(type) === normalizedKey)
      : false;
  }

  if (groupMode === "country") {
    if (normalizedKey === "unassigned") return !node.country;
    return normalizeKey(node.country) === normalizedKey;
  }

  return false;
};

const countGroupMatches = (groupNode, groupMode) => {
  if (!groupNode) return 0;
  const groupKey = groupNode.groupKey;
  const useMatches = hasMatchFlag(groupNode);
  let count = 0;

  const visit = (node) => {
    if (!node) return;

    if (!node.isGroup) {
      if (useMatches) {
        if (node.matchesGroup) count += 1;
      } else if (matchesGroupFallback(node, groupMode, groupKey)) {
        count += 1;
      }
    }

    if (Array.isArray(node.children)) {
      node.children.forEach(visit);
    }
  };

  if (Array.isArray(groupNode.children)) {
    groupNode.children.forEach(visit);
  }

  return count;
};

const OrganizationPage = observer(() => {
  const { t } = useTran();
  const organizationStore = useOrganizationStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [dialogInitial, setDialogInitial] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [groupMode, setGroupMode] = useState("level");

  const resolvedTree =
    groupMode === "businessType"
      ? organizationStore.businessTypeTree
      : groupMode === "country"
        ? organizationStore.countryTree
        : organizationStore.tree;

  const groupCounts = useMemo(() => {
    if (groupMode === "level") {
      const counts = new Map();
      organizationStore.items.forEach((unit) => {
        const level = getLevelNumber(unit.unitLevel);
        if (!level) return;
        counts.set(level, (counts.get(level) || 0) + 1);
      });

      return Array.from(counts.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([level, count]) => ({
          key: `level_${level}`,
          label: `${t("Level")} ${level}`,
          count,
        }));
    }

    const root = resolvedTree?.[0];
    const groups = Array.isArray(root?.children) ? root.children : [];
    return groups.map((group) => ({
      key: group.id || group.groupKey || group.name,
      label: group.name,
      count: countGroupMatches(group, groupMode),
    }));
  }, [groupMode, organizationStore.items, resolvedTree, t]);

  useEffect(() => {
    organizationStore.fetchLookups().catch(() => {});
  }, [organizationStore]);

  useEffect(() => {
    const groupBy = resolveGroupBy(groupMode);
    organizationStore
      .fetchHierarchy({
        groupBy,
        selectId: organizationStore.selectedId,
      })
      .catch(() => {});
  }, [organizationStore, groupMode]);

  const handleCreate = (typeHint) => {
    setDialogMode("create");
    setDialogInitial({
      unitLevel: typeHint || "level_1",
      parentId: "",
      region: "",
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (unit) => {
    if (!unit) return;
    setDialogMode("edit");
    setDialogInitial({
      ...unit,
      parentId: unit.parentId || "",
      region: unit.region || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = (unit) => {
    setPendingDelete(unit);
    setConfirmOpen(true);
  };

  const handleDialogSubmit = async (payload) => {
    const groupBy = resolveGroupBy(groupMode);
    if (dialogMode === "create") {
      await organizationStore.createUnit(payload, { groupBy });
    } else {
      await organizationStore.updateUnit(dialogInitial.id, payload, {
        groupBy,
      });
    }
    setDialogOpen(false);
  };

  const confirmDeleteUnit = async () => {
    if (!pendingDelete) return;
    const groupBy = resolveGroupBy(groupMode);
    await organizationStore.deleteUnit(pendingDelete.id, { groupBy });
    setConfirmOpen(false);
    setPendingDelete(null);
  };

  const handleRefresh = () => {
    const groupBy = resolveGroupBy(groupMode);
    organizationStore
      .fetchHierarchy({
        selectId: organizationStore.selectedId,
        groupBy,
      })
      .catch(() => {});
  };

  const groupOptions = [
    { value: "level", label: t("By Level") },
    { value: "businessType", label: t("By Business Type") },
    { value: "country", label: t("By Country") },
  ];

  const groupSummary =
    groupCounts.length > 0 ? (
      <div className="flex flex-wrap justify-end gap-2">
        {groupCounts.map((item) => (
          <Badge
            key={item.key}
            variant="secondary"
            className="rounded-full px-2 py-0.5 text-[11px]"
          >
            {item.label}: {item.count}
          </Badge>
        ))}
      </div>
    ) : null;

  const groupToggle = (
    <div className="flex flex-col items-end gap-2">
      <Select value={groupMode} onValueChange={setGroupMode}>
        <SelectTrigger
          size="sm"
          className="min-w-[180px] rounded-2xl border border-border bg-card"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {groupOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {viewMode === "diagram" && groupSummary}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Organization Structure")}
        description={t(
          "Model your parent company and multi-level organization structure.",
        )}
        icon={Network}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={organizationStore.loading}
            >
              <RefreshCcw
                className={`${organizationStore.loading ? "animate-spin" : ""}`}
              />
              {t("Refresh")}
            </Button>
            <div className="flex items-center rounded-2xl border border-border bg-card">
              <Button
                type="button"
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                className="rounded-2xl"
                onClick={() => setViewMode("list")}
              >
                <List size={14} />
                {t("List")}
              </Button>
              <Button
                type="button"
                variant={viewMode === "diagram" ? "default" : "ghost"}
                size="sm"
                className="rounded-2xl"
                onClick={() => setViewMode("diagram")}
              >
                <Network size={14} />
                {t("Diagram")}
              </Button>
            </div>
            <Button size="sm" type="button" onClick={() => handleCreate()}>
              <PlusCircle />
              {t("Add Unit")}
            </Button>
          </div>
        }
      />

      {organizationStore.error && !dialogOpen ? (
        <div className="flex items-center gap-3 rounded-radius border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle size={18} />
          <span>{organizationStore.error}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-xs"
            onClick={() => organizationStore.resetError()}
          >
            {t("Dismiss")}
          </Button>
        </div>
      ) : null}
      {organizationStore.lookupError ? (
        <div className="flex items-center gap-3 rounded-radius border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <span>{organizationStore.lookupError}</span>
        </div>
      ) : null}

      {viewMode === "list" ? (
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
          <ResizableSidebar
            storageKey="nxstart_org_tree_width"
            handleClassName="lg:hidden xl:block"
            handleTitle={t("Drag to resize")}
          >
            <OrganizationTree tree={resolvedTree} headerActions={groupToggle} />
          </ResizableSidebar>
          <div className="flex-1">
            <OrganizationDetails onEdit={handleEdit} onDelete={handleDelete} />
          </div>
        </div>
      ) : (
        <div className="space-y-3 rounded-radius border border-border bg-background p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("Relationship Diagram")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  "Visualize how parent companies and organization levels connect.",
                )}
              </p>
            </div>
            {groupToggle}
          </div>
          <OrganizationDiagram tree={resolvedTree} groupMode={groupMode} />
        </div>
      )}

      <OrganizationUnitDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogInitial}
        onSubmit={handleDialogSubmit}
        onOpenChange={setDialogOpen}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("Delete organization unit")}
        description={t(
          "This action is permanent. Child units must be reassigned or removed before deleting.",
        )}
        tone="danger"
        confirmLabel={
          organizationStore.deletingId === pendingDelete?.id
            ? t("Deleting...")
            : t("Delete")
        }
        cancelLabel={t("Cancel")}
        loading={organizationStore.deletingId === pendingDelete?.id}
        onConfirm={confirmDeleteUnit}
      />
    </div>
  );
});

export default OrganizationPage;
